import type { ProjectMember } from '../transport/api'

/** One human in the Members sheet plus the agents THEY attached, seated beneath them. */
export type RosterGroup = { human: ProjectMember; agents: ProjectMember[] }

/** The key a group folds under — stable across refetches (id first, email second). */
export function ownerKeyOf(h: ProjectMember): string {
  return (typeof h.user === 'string' && h.user) || String(h.email ?? '').toLowerCase()
}

/**
 * Seat every agent under its owner — owner, 2026-08-19: 把 Owner 的 Agents 放在 Owner 的
 * 下方，往右縮一格，可收，這樣可以快速辨認 Agent 是誰的.
 *
 * The flat list put all humans first and all agents second, and an agent carried no
 * readable WHOSE (the chat v1 mapping kept only `mine`), so with two people and four
 * agents the room was unreadable. A tree — person, then their agents indented below —
 * makes ownership a matter of position, not of reading a second line.
 *
 * Ownership resolves in order of how trustworthy the evidence is:
 *   1. `owner_user` = a human row's `user` (chat v1: both are the same account id);
 *   2. `owner_email` = a human row's email (the legacy edge path, vault identities);
 *   3. `mine` = the viewer's own row (the edge stamps it from its attachment table —
 *      the one signal that survived the vaultless router naming owners `device:<fp>`).
 * Whatever resolves to nobody in the room — owner left, or an older wire with no
 * identity at all — lands in `orphans`, which the sheet still renders in full. Grouping
 * must never HIDE an agent; it only decides where it sits.
 */
export function groupRoster(
  members: ProjectMember[],
  selfEmail: string | null,
): { groups: RosterGroup[]; orphans: ProjectMember[] } {
  const humans = members.filter((m) => m.kind !== 'agent')
  const agents = members.filter((m) => m.kind === 'agent')
  const groups: RosterGroup[] = humans.map((human) => ({ human, agents: [] }))

  const byUser = new Map<string, number>()
  const byEmail = new Map<string, number>()
  humans.forEach((h, i) => {
    if (typeof h.user === 'string' && h.user) byUser.set(h.user, i)
    const e = String(h.email ?? '').toLowerCase()
    if (e && !byEmail.has(e)) byEmail.set(e, i)
  })
  const self = (selfEmail ?? '').toLowerCase()
  let selfIdx = self ? byEmail.get(self) : undefined
  if (selfIdx == null) {
    const i = humans.findIndex((h) => h.mine === true)
    if (i >= 0) selfIdx = i
  }

  const orphans: ProjectMember[] = []
  for (const a of agents) {
    let i = typeof a.owner_user === 'string' && a.owner_user ? byUser.get(a.owner_user) : undefined
    if (i == null) {
      const e = String(a.owner_email ?? '').toLowerCase()
      if (e) i = byEmail.get(e)
    }
    if (i == null && a.mine === true) i = selfIdx
    if (i == null) orphans.push(a)
    else groups[i]!.agents.push(a)
  }
  return { groups, orphans }
}

/**
 * The display name for the human who OWNS an agent row — the busy float's and the
 * @ picker's shared rule (owner, 2026-08-26/27: 下標 Owner 的名稱，而非機器的名稱).
 * Profile name first, email local-part as the stand-in; null when nobody resolves
 * (a machine label is then more honest than a guess).
 */
export function ownerDisplayName(members: ProjectMember[], m: ProjectMember): string | null {
  const human = members.find(
    (h) =>
      h.kind !== 'agent' &&
      ((m.owner_user && h.user === m.owner_user) ||
        (!!m.owner_email &&
          String(h.email ?? '').toLowerCase() === String(m.owner_email).toLowerCase())),
  )
  const label = human?.label || human?.email || m.owner_label || m.owner_email
  if (!label) return null
  const text = String(label)
  return text.includes('@') && !text.startsWith('device:') ? text.split('@')[0]! : text
}
